/**
 * Utility functions for converting between total guests and guests per room
 * to handle the semantic mismatch between frontend (total guests) and API (guests per room)
 */

/**
 * Convert total guests to guests per room for API calls
 * @param totalGuests - Total number of guests for the entire reservation
 * @param roomQuantity - Number of rooms being booked
 * @returns Number of guests per room (rounded up to ensure no guest is left out)
 * @throws Error if roomQuantity is 0 or negative
 */
export const calculateGuestsPerRoom = (
  totalGuests: number,
  roomQuantity: number,
): number => {
  // Validate inputs
  if (roomQuantity <= 0) {
    throw new Error('Room quantity must be greater than 0');
  }

  if (totalGuests < 0) {
    throw new Error('Total guests cannot be negative');
  }

  if (totalGuests === 0) {
    return 0;
  }

  // Round up to ensure no guest is left without a room
  // Example: 7 guests ÷ 3 rooms = 2.33 → 3 guests per room
  const guestsPerRoom = Math.ceil(totalGuests / roomQuantity);

  return guestsPerRoom;
};

/**
 * Convert guests per room to total guests for UI display
 * @param guestsPerRoom - Number of guests per room (from API response)
 * @param roomQuantity - Number of rooms being booked
 * @returns Total number of guests across all rooms
 * @throws Error if roomQuantity is 0 or negative
 */
export const calculateTotalGuests = (
  guestsPerRoom: number,
  roomQuantity: number,
): number => {
  // Validate inputs
  if (roomQuantity <= 0) {
    throw new Error('Room quantity must be greater than 0');
  }

  if (guestsPerRoom < 0) {
    throw new Error('Guests per room cannot be negative');
  }

  if (guestsPerRoom === 0) {
    return 0;
  }

  const totalGuests = guestsPerRoom * roomQuantity;

  return totalGuests;
};
